import { useQuery } from "@rocicorp/zero/react";
import { useEffect, useRef, useState } from "react";
import type { AppTag } from "../../../shared/connectors/tags";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useCreateConnector } from "../../hooks/useCreateConnector";
import { useManagedConnectorsEnabled } from "../../hooks/useManagedConnectorsEnabled";
import {
  listedConnectorDirectory,
  managedConnectorDirectory,
} from "../../lib/connectorDirectory";
import { navigate, Route, routes, useCurrentUrl } from "../../lib/urls";
import { useModalId } from "../globalModalStack";
import { MainScrollContainer } from "../MainScrollContainer";
import { Directory } from "./Directory";

const browseDirectory = listedConnectorDirectory.toSorted(
  (a, b) =>
    Number(b.tags.includes("featured")) - Number(a.tags.includes("featured"))
);

export function DirectoryPage({ active = true }: { active?: boolean }) {
  const { canCreate: canCreateApp, create } = useCreateConnector();
  const { q: searchQuery, tag: selectedTag } = useCurrentUrl(
    routes.connectors.browse
  );
  const [createdAppId, setCreatedAppId] = useState<string | null>(null);
  const directory = useManagedConnectorsEnabled()
    ? [...managedConnectorDirectory, ...browseDirectory]
    : browseDirectory;

  let visibleApps = directory;
  if (selectedTag !== "all") {
    visibleApps = visibleApps.filter((app) =>
      app.tags.includes(selectedTag as AppTag)
    );
  }
  if (searchQuery?.trim()) {
    const query = searchQuery.toLowerCase();
    visibleApps = visibleApps.filter(
      (app) =>
        app.name.toLowerCase().includes(query) ||
        app.definition.keywords?.toLowerCase().includes(query)
    );
  }

  const customApp = listedConnectorDirectory.find(
    (app) => app.identifier === "apiKey"
  );

  const [connector] = useQuery(
    queries.loadConnectorDetails({ connectedAppId: createdAppId ?? "" }),
    { enabled: !!createdAppId }
  );

  const connectorModalOpen = useModalId("connector");

  useEffect(() => {
    if (createdAppId && !connectorModalOpen) {
      const justCreated = connector && !connector.fresh ? connector.id : "";
      navigate(routes.connectors.browse, { justCreated }, { replace: true });
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: clears the created-app id after navigating once the connector modal closes
      setCreatedAppId(null);
    }
  }, [createdAppId, connectorModalOpen, connector]);

  const pendingHandled = useRef(false);

  const captureCreate = ({ identifier }: { identifier?: string }) => {
    if (pendingHandled.current) return null;
    if (!canCreateApp) return null;
    pendingHandled.current = true;

    const appDef = directory.find((a) => a.identifier === identifier);
    if (!appDef) return null;

    const newAppId = create(appDef);
    pendingHandled.current = false;
    setCreatedAppId(newAppId);
    navigate(routes.modal.connector, { id: newAppId });
    navigate(routes.connectors.browse, {}, { replace: true });
    return null;
  };

  return (
    <>
      {active && <title>{`Browse all • ${config.title}`}</title>}
      <MainScrollContainer ready active={active} resetOnNavigate={false}>
        <Route path={routes.connectors.browse.create}>{captureCreate}</Route>

        <Directory
          apps={
            visibleApps.length === 0 && customApp ? [customApp] : visibleApps
          }
          disabled={!canCreateApp}
        />
      </MainScrollContainer>
    </>
  );
}
